import { Stack } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { type Value } from '@/james-playground/aa-family-claim/claim/facts';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { labelOf } from '@/james-playground/aa-family-claim/claim/labels';
import {
  CARD,
  MANAGES,
  NEEDED_HINT,
  REASON,
  REASON_SHORT,
  WATCH,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  gateQuestion,
  ChoiceCards,
  Count,
  Part,
  Parts,
  SuggestField,
  Toggle,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import { str } from '@/james-playground/aa-family-claim/claim/cards/values';

/** The reason entries end in the reason: supervisionReasonWandering. */
const reasonKey = (id: string) =>
  id.replace(/^(supervisionReason|nightWatchReason)/, '');
const isReason = (id: string) =>
  /Reason/.test(id) && Boolean(REASON[reasonKey(id)]);

/**
 * Someone keeping an eye on her in the day, or awake to watch over her at
 * night. On a Yes: why, from the form's own list said plainly, then how long
 * she can be left (in the day) or how often and how long someone is awake for
 * her (at night).
 */
const WatchCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const { draft, set, visible } = useDraft(ask, facts);
  const qid = useQids();
  const gateId = ask.gate ?? '';
  const gate = str(draft[gateId]);
  const night = ask.area === 'night';
  const ids = ask.entries.filter(visible);
  const reasons = ids.filter(isReason);
  const ticked = reasons.filter((id) => str(draft[id]) === 'Yes');
  const extras = ids.filter((id) => id !== gateId && !isReason(id));
  const timesId = extras.find(
    (id) => entry(id).answerType === 'count_per_night',
  );
  const lengthId = extras.find(
    (id) => entry(id).answerType !== 'count_per_night',
  );

  const ready =
    gate === 'No' ||
    (gate === 'Yes' &&
      ticked.length > 0 &&
      (!lengthId || str(draft[lengthId]).trim() !== '') &&
      (!timesId || Number(str(draft[timesId])) > 0));

  const submit = () => {
    if (gate !== 'Yes') return onSubmit({ [gateId]: 'No' }, say(MANAGES, v));
    const values: Record<string, Value> = { [gateId]: 'Yes' };
    for (const id of reasons) values[id] = ticked.includes(id) ? 'Yes' : 'No';
    if (lengthId) values[lengthId] = str(draft[lengthId]).trim();
    if (timesId) values[timesId] = str(draft[timesId]);
    const said = ticked.map((id) => REASON_SHORT[reasonKey(id)]);
    const length = str(draft[lengthId ?? '']).trim();
    const how = night
      ? `${str(draft[timesId ?? ''])} ${WATCH.timesUnit}, ${length.toLowerCase().replace(/ each time$/, '')} each time`
      : length === WATCH.aloneChips[0]
        ? say(WATCH.cannotBeLeft, v)
        : `${labelOf(lengthId ?? '', v)} ${length.toLowerCase()}`;
    onSubmit(values, `Yes: ${said.join(', ')}. ${how}`);
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Toggle
        {...gateQuestion(ask, v)}
        hint={say(NEEDED_HINT, v)}
        value={gate}
        onChange={(value) => set(gateId, value)}
      />
      {gate === 'Yes' && (
        <Parts>
          <Part
            qid={qid('why')}
            question={say(WATCH.reasons[night ? 'night' : 'day'], v)}
          >
            <ChoiceCards
              multi
              labelledBy={qid('why')}
              options={reasons.map((id) => ({
                value: id,
                label: say(REASON[reasonKey(id)], v),
              }))}
              selected={ticked}
              onPick={(id) => set(id, str(draft[id]) === 'Yes' ? 'No' : 'Yes')}
            />
          </Part>
          {timesId && (
            <Part qid={qid('times')} question={say(WATCH.times, v)}>
              <Count
                label={say(WATCH.times, v)}
                unit={WATCH.timesUnit}
                value={str(draft[timesId])}
                onChange={(value) => set(timesId, value)}
                min={1}
                max={20}
              />
            </Part>
          )}
          {lengthId && (
            <Part
              qid={qid('length')}
              question={night ? WATCH.howLong : say(WATCH.aloneFor, v)}
            >
              <Stack>
                <SuggestField
                  labelledBy={qid('length')}
                  chips={night ? WATCH.howLongChips : WATCH.aloneChips}
                  other={CARD.somethingElse}
                  field={night ? WATCH.howLongField : WATCH.aloneField}
                  value={str(draft[lengthId])}
                  onChange={(value) => set(lengthId, value)}
                />
              </Stack>
            </Part>
          )}
        </Parts>
      )}
    </CardShell>
  );
};

WatchCard.displayName = 'WatchCard';
export default WatchCard;
